/**
 * The permission vocabulary — docs/005-authorization.md §Permissions.
 *
 * Permissions are **code, not data**. Adding one is a one-line change here with
 * compile-time safety at every call site; a typo does not compile. There is no
 * such thing as a permission migration, so there is no such thing as a
 * permission repair script — which is precisely the failure class that killed
 * the prior attempt (docs/005 §"Why code, not data").
 *
 * Format: `<app>.<resource>.<action>`.
 *   apps    — platform | visitor | directory | survey | mailroom
 *   actions — create | read | update | delete | manage   (`manage` implies the
 *             other four; the implication is applied during role expansion, see
 *             `roles.ts`)
 */
import { APP_IDS } from '@instanexus/contracts';
/** The five apps a permission may belong to (docs/005 §Permissions line 13).
 *
 *  DECLARED IN `@instanexus/contracts` (./app.ts) SINCE S38-TASK-002 and re-exported here
 *  under its original name. `authz` answers "may this USER do this" and `contracts` carries the
 *  wire vocabulary; both need the same closed list of applications, `authz` already depends on
 *  `contracts`, and the reverse would be a cycle. One list, two names, no drift — the lock-step
 *  is asserted in permissions.test.ts. */
export const PERMISSION_APPS = APP_IDS;
/**
 * The actions a permission may end in (docs/005 §Permissions line 13).
 *
 * `self-check-out` is the one deliberate exception to the CRUD+`manage` vocabulary: it names
 * a DEVICE-ONLY capability (`visitor.visit.self-check-out`, docs/plans/kiosk-self-check-out.md
 * "Decisions") that a paired kiosk exercises on a visitor's own say-so, with no staff
 * verification — a materially different act from any CRUD verb on the same resource, and
 * `MANAGE_IMPLIES` below deliberately does NOT include it, so `visitor.visit.manage` can
 * never imply it by accident. See `DEVICE_ONLY_PERMISSIONS` for how it is kept off every
 * human role even through a `visitor.*` wildcard.
 *
 * `print` is the same kind of exception for `visitor.badge.print` (S35 PRT-01b/04/12): it names
 * a DEVICE-ONLY capability — a paired kiosk resolving and, later, producing its own badge —
 * distinct from any CRUD verb on `badge`, and `MANAGE_IMPLIES` below deliberately does NOT
 * include it either, for the identical reason `self-check-out` is excluded.
 */
export const PERMISSION_ACTIONS = ['create', 'read', 'update', 'delete', 'manage', 'self-check-out', 'print'];
/**
 * The actions `manage` expands to. `manage` itself is not in this list — it is
 * granted directly, not by implication.
 */
export const MANAGE_IMPLIES = ['create', 'read', 'update', 'delete'];
export const PERMISSIONS = [
    // --- platform ------------------------------------------------------------
    'platform.person.read',
    'platform.person.manage',
    'platform.location.read',
    'platform.location.manage',
    'platform.department.read',
    'platform.department.manage',
    'platform.user.manage',
    'platform.billing.manage',
    /**
     * Reading a tenant *other than your own*. Cross-tenant, therefore
     * operator-only — see `OPERATOR_ONLY_PERMISSIONS` below.
     */
    'platform.tenant.read',
    /**
     * `GET /api/web/memberships`. docs/005 left this as a TODO and suggested
     * `platform.user.manage`; a read route must not require a manage permission,
     * so it gets its own read permission instead. Note that `platform.user.manage`
     * does **not** imply it — the `manage`⇒CRUD implication is per-resource, and
     * `user` and `membership` are different resources. A role that needs both
     * lists both (or reaches them through a `platform.*` pattern).
     */
    'platform.membership.read',
    /**
     * Editing the GLOBAL price list (`PlanEntitlement`) and granting a per-tenant
     * override (`Entitlement`). Cross-tenant by definition — a tenant owner must
     * never be able to reprice their own plan or grant themselves a trial — so
     * it is operator-only, see `OPERATOR_ONLY_PERMISSIONS` below.
     *
     * DISTINCT from `platform.billing.manage`, which is the TENANT-side permission
     * for managing one's own subscription (checkout, the portal, reading one's own
     * entitlements). Both exist on purpose; neither implies the other.
     */
    'platform.entitlement.manage',
    /**
     * Bulk employee import (S30 Phase 3): `POST /people/import/preview`,
     * `POST /people/import` and `GET /people/import/template`. Deliberately its OWN
     * resource, not folded into `platform.person.manage` — one request rewrites hundreds
     * of rows, a different blast radius from a per-record edit, and `PERMISSION_ACTIONS`
     * is a closed five-member set so `platform.person.import` cannot exist (docs/005).
     */
    'platform.import.manage',
    /**
     * `GET /api/web/onboarding/status` — the guided-setup wizard's whole progress (S37).
     *
     * ITS OWN RESOURCE, not a reuse of `platform.location.read`. The response is a summary
     * ACROSS four resources — the HQ location, its branding, the employee-contact count, a
     * paired device and the first visit — so any single existing read permission would be
     * both too narrow to justify the other three facts and too wide in reach: every role that
     * can read locations would inherit it, including `visitor.frontdesk` and `visitor.viewer`,
     * who cannot perform a single step of the wizard.
     *
     * GRANTED TO EXACTLY THE ROLES THAT CAN FINISH ONBOARDING — `tenant.owner` (through its
     * `platform.*` pattern) and `tenant.admin` (which names it literally in `roles.ts`, since
     * its patterns are per-resource). Completing the wizard needs `platform.location.manage`,
     * `platform.person.manage` AND `visitor.device.manage`; no other system role holds all
     * three, so no other system role holds this.
     */
    'platform.onboarding.read',
    /**
     * `GET /api/web/audit-events` — a tenant's own audit trail (S42-m4dr Phase 4). TENANT-scoped
     * (never cross-tenant — that is `platform.tenant.read`, operator-only), so it is an ordinary
     * grantable permission, not a member of `OPERATOR_ONLY_PERMISSIONS`. Granted to `tenant.owner`
     * (through its `platform.*` pattern) and named literally on `tenant.admin`, the same shape as
     * `platform.onboarding.read` immediately above.
     */
    'platform.audit.read',
    /**
     * The tenant's OWN platform-level settings — today the Privacy section of platform-admin
     * Settings (`GET|PATCH /api/web/tenant/privacy-settings`, docs/adr/0032 Slice 3: whether the
     * tenant's kiosks send usage analytics). Its own resource, not folded into
     * `platform.location.manage` or `visitor.settings.manage`: it governs every app's devices
     * tenant-wide, and a privacy decision is a different blast radius from editing a location.
     * TENANT-scoped, so ordinary and grantable. Reached by `tenant.owner` through `platform.*`
     * and named on `tenant.admin` as `platform.settings.*`; no visitor role holds it.
     */
    'platform.settings.read',
    'platform.settings.manage',
    /** Operator Partners console: read partners and their linked tenants (docs/adr/0035). OPERATOR_ONLY. */
    'platform.partner.read',
    /** Operator Partners console: create/delete partners, link/unlink tenants (docs/adr/0035). OPERATOR_ONLY. */
    'platform.partner.manage',
    /** Operator Partners console: mark a referral partner's commission as paid (docs/adr/0035). OPERATOR_ONLY. */
    'platform.partner-commission.manage',
    /**
     * Operator Support console: pause or resume a tenant's company sign-in enforcement so a locked-out
     * customer can use passwords for a bounded time (S50-kqp9 Phase 4b, ADR 0042 decision 14).
     * OPERATOR_ONLY, and the one write a SUPPORT operator may perform (`SUPPORT_WRITE_PERMISSIONS`).
     * Never a tenant permission: once enforcement is on nobody in the tenant has a password, so the
     * way back in must not be holdable by anyone in the tenant.
     */
    'platform.tenant-sso.manage',
    /**
     * The Control Tower door (S52-jecr, GitHub #162): the cross-tenant super-admin app. OPERATOR_ONLY and
     * NOT a SUPPORT write. The action is `manage`, not `read`, so SUPPORT (which holds every operator
     * `read`) cannot reach it; only a SUPERADMIN operator does.
     */
    'platform.control-tower.manage',
    /** Customer: the "Your partner" card in the tenant's own settings (docs/adr/0035). Grantable. */
    'platform.partner-access.read',
    /** Customer: the setup-access switch for the tenant's partner (docs/adr/0035). Grantable. */
    'platform.partner-access.manage',
    /** Partner portal: list the partner's customers (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-customer.read',
    /** Partner portal: add a customer to the partner (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-customer.create',
    /** Partner portal: "Open setup" for a customer (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-setup.create',
    /** Partner portal: manage the partner's own team (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-team.manage',
    /** Partner portal: invoices and earnings statements (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-statement.read',
    /** Partner portal: manage the partner's own billing account and payment method (docs/adr/0035). PARTNER_ONLY. */
    'platform.partner-billing.manage',
    /**
     * The tenant's chat integrations — Settings → Integrations (PRD-010): which Slack / Teams
     * workspace is installed, its location filter, connect / disconnect / test. ITS OWN RESOURCE,
     * not folded into `platform.settings.*`: installing a chat app hands a third-party workspace a
     * bot that will be told who is visiting whom, which is a data-sharing decision with a wider
     * blast radius than a privacy switch. TENANT-scoped and grantable. Reached by `tenant.owner`
     * through `platform.*` and named on `tenant.admin` as `platform.integrations.*`.
     */
    'platform.integrations.read',
    'platform.integrations.manage',
    /**
     * External directory sync (S49-3u50): the tenant's Google Workspace / Entra ID / SCIM
     * connection, its scope and location mapping, previews, applies and run history. ITS OWN
     * RESOURCE, not `platform.person.*`: connecting hands a third-party identity system the power
     * to create and deactivate every employee record. TENANT-scoped and grantable. Reached by
     * `tenant.owner` through `platform.*` and named on `tenant.admin`. Partner-setup excluded
     * (employee data is customer data, adr/0035 decision 5).
     */
    'platform.directory-sync.read',
    'platform.directory-sync.manage',
    /**
     * Company sign-in (S50-kqp9 Phase 4): the tenant's claimed Google Workspace domain (later a
     * Microsoft 365 tenant), its proof, the admin's test sign-in, and the switch that makes company
     * sign-in mandatory for everyone. ITS OWN RESOURCE, not `platform.settings.*`: turning it on
     * ends every password session in the tenant and stops every password sign-in, owners included.
     * TENANT-scoped and grantable. Reached by `tenant.owner` through `platform.*` and named on
     * `tenant.admin`. Partner-setup excluded (a partner must never be able to lock a customer out
     * of their own tenant).
     */
    'platform.sso.read',
    'platform.sso.manage',
    /**
     * The tenant's allowed email domains (handoff-0017 §2): the list a free-form address on a
     * location's check-in notification list must match. ITS OWN RESOURCE, not
     * `platform.settings.*` and not `visitor.settings.*`: adding a domain is what would let an
     * outside inbox receive every visitor's name and arrival time, so whoever can add recipients
     * must not also be able to widen where they may point. TENANT-scoped and grantable. Reached by
     * `tenant.owner` through `platform.*` and named on `tenant.admin`. Partner-setup excluded.
     */
    'platform.email-domain.read',
    'platform.email-domain.manage',
    /**
     * A user's own push installations on the employee app (adr/0037 decision 7). Self-only is
     * route-shaped. Grantable; never a device or partner-setup permission.
     */
    'platform.push-installation.manage',
    // --- visitor -------------------------------------------------------------
    'visitor.visit.read',
    'visitor.visit.create',
    'visitor.visit.manage',
    /**
     * The Visitor-host module on the employee app: the visits where the caller is the host
     * (adr/0037 decision 5). Self-scoped by its routes; not `visitor.visit.read`, which is every
     * visit at a location.
     */
    'visitor.hosted-visit.read',
    /**
     * Reply to (and, from Phase 3, act on) the visits where the caller is the host — the employee
     * app's quick replies (adr/0037). Self-scoped by its routes, like `.read`; not
     * `visitor.visit.manage`, which is every visit at a location.
     */
    'visitor.hosted-visit.update',
    'visitor.flow.read',
    'visitor.flow.manage',
    'visitor.device.manage',
    /**
     * The visitor app's own settings (S12 Phase 4). NOT folded into `visitor.flow.manage`:
     * authoring a tenant's check-in screens and silencing every host notification for the
     * whole tenant are different blast radii, and a role that may do the first must not
     * silently gain the second. `manage` implies `read` per-resource during role expansion,
     * so a role granted only `visitor.settings.manage` can still open the screen.
     *
     * Reached by `tenant.owner`, `tenant.admin` and `visitor.admin` through their existing
     * `visitor.*` patterns; deliberately NOT held by `visitor.frontdesk`, `visitor.manager`
     * or `visitor.viewer`, which list their permissions literally.
     */
    'visitor.settings.read',
    'visitor.settings.manage',
    /**
     * Registering, editing, promoting to default and removing a location's badge printers —
     * PRD-009 PRT-01, PRT-01a, PRT-01c, PRT-02 (`POST|PATCH|DELETE /visitor/badge-printers`).
     * Its own resource, NOT a reuse of `platform.location.manage` (which is what these routes
     * declared until 2026-10-09): a badge printer is a visitor-app concern, and the visitor admin
     * who runs the lobby must be able to manage one. Granting `platform.location.manage` to
     * `visitor.admin` to get there would hand it tenant-wide location create/delete, location
     * branding and kiosk settings along with the printers — a far wider blast radius than a
     * printer list.
     *
     * There is deliberately no `visitor.printer.read`: printer READS stay on
     * `platform.location.read`, which every visitor role already holds (a kiosk's printer picker
     * and the read-only Printers page need it), so `manage` implies nothing extra here. Reached by
     * `tenant.owner`, `tenant.admin` and `visitor.admin` through their `visitor.*` patterns; NOT
     * held by `visitor.manager`, `visitor.frontdesk` or `visitor.viewer`, which list their
     * permissions literally, nor by any device role.
     */
    'visitor.printer.manage',
    /**
     * A paired kiosk checking a visitor out on their OWN say-so, with no staff verification —
     * PRD-004 CHK-16, docs/plans/kiosk-self-check-out.md "Decisions". Distinct from
     * `visitor.visit.manage` (front desk checking out anyone, anywhere): a kiosk trusts only a
     * re-typed, whole-word-matched name for its own location, a materially smaller and
     * differently-verified act. See `DEVICE_ONLY_PERMISSIONS` immediately below — this
     * permission is never tenant-grantable to a human role.
     */
    'visitor.visit.self-check-out',
    /**
     * A paired kiosk resolving which printer it prints badges to, and later reporting that it
     * did — PRD-009 PRT-01b, PRT-04, PRT-12 (S35). One capability, not two: reading the resolved
     * printer configuration and reporting a print are two halves of the same purpose held by the
     * same actor, so this task's route and Phase 6's print-record route share this one
     * permission rather than splitting into a paired read/write pair. `print` is not a CRUD verb
     * on `badge` — a kiosk cannot create, update or delete a printer registration this way — so
     * it gets its own action, mirroring `self-check-out` immediately above. See
     * `DEVICE_ONLY_PERMISSIONS` immediately below — this permission is never tenant-grantable to
     * a human role, not even through `tenant.owner`'s `visitor.*` wildcard.
     */
    'visitor.badge.print',
    // --- directory --- (S47-rsq5, docs/adr/0036)
    /** Browse, search, profiles, org chart, photos (S47-rsq5). */
    'directory.person.read',
    /** A directory admin acts on any person's directory profile (photo moderation, Phase 4). */
    'directory.person.manage',
    /** Edit your OWN directory profile; self-only is route-shaped (/directory/me). */
    'directory.profile.update',
    /** Invite employees to sign in to the Directory (Phase 3/5). */
    'directory.access.manage',
    /** View tenant-level Directory settings. */
    'directory.settings.read',
    /** Change tenant-level Directory settings, e.g. "Show contractors" (Phase 2). */
    'directory.settings.manage',
];
/** Membership test set. Built once; `can()` is set math, never a scan. */
export const PERMISSION_SET = new Set(PERMISSIONS);
/**
 * Cross-tenant platform-operator capabilities. **Load-bearing — read before
 * editing.**
 *
 * docs/005 §Platform operators: platform access "cannot be expressed as a
 * `RoleAssignment`". But `SYSTEM_ROLES['tenant.owner']` carries the pattern
 * `platform.*`, which would naively expand to include `platform.tenant.read`
 * and hand every tenant owner cross-tenant read access. That is exactly the
 * "roles never gain permissions silently" failure docs/005 §Enforcement exists
 * to prevent.
 *
 * So: wildcard expansion in `roles.ts` **skips** every permission in this set. A
 * pattern can only reach one by naming it literally, and `assertRolesAreLive()`
 * then fails the process at startup for doing so. There is no way for a role to
 * hold one of these, by construction.
 *
 * The only path to an operator-only permission is `AuthContext.isPlatformOperator`
 * with a `platformRole`, which is a column on `User` and not tenant-grantable.
 */
export const OPERATOR_ONLY_PERMISSIONS = new Set([
    'platform.tenant.read',
    'platform.entitlement.manage',
    'platform.partner.read',
    'platform.partner.manage',
    'platform.partner-commission.manage',
    'platform.tenant-sso.manage',
    'platform.control-tower.manage',
]);
/**
 * The writes a SUPPORT operator may perform (docs/005 "Platform operators"). Everything else SUPPORT
 * holds is a `read`. Every member must also be in OPERATOR_ONLY_PERMISSIONS (asserted in permissions.test.ts):
 * an operator write must never be reachable by a tenant role.
 */
export const SUPPORT_WRITE_PERMISSIONS = new Set([
    'platform.tenant-sso.manage',
]);
/** The Control Tower door (S52-jecr): named once for the API guard and the SPA. */
export const CONTROL_TOWER_PERMISSION = 'platform.control-tower.manage';
/**
 * Permissions that exist only for a paired DEVICE principal (docs/005 §"Paired devices") —
 * never reachable by a human role, not even through a `visitor.*` wildcard. **Load-bearing —
 * read before editing.**
 *
 * `SYSTEM_ROLES['tenant.owner']`, `['tenant.admin']` and `['visitor.admin']` all carry the
 * pattern `visitor.*`, which would naively sweep up `visitor.visit.self-check-out` along with
 * every other `visitor.*` permission — handing every tenant owner, tenant admin and visitor
 * admin the same unverified, on-the-visitor's-own-say-so check-out a kiosk gets. Nobody would
 * have named it anywhere in `SYSTEM_ROLES` for that to happen; the wildcard alone would do
 * it. That is the exact "roles never gain permissions silently" failure
 * `OPERATOR_ONLY_PERMISSIONS` above exists to prevent, mirrored here for the opposite
 * direction: this set closes the human side, `OPERATOR_ONLY_PERMISSIONS` closes the
 * cross-tenant side, and neither implies the other.
 *
 * Symmetric to `OPERATOR_ONLY_PERMISSIONS` but the roles are reversed: `roles.ts` skips every
 * permission in this set when expanding a `SYSTEM_ROLES` pattern, and `assertRolesAreLive()`
 * fails the process if a `SYSTEM_ROLES` pattern names one literally. `DEVICE_ROLES` is exempt
 * from both — naming one literally there is the only way it is ever granted, exactly like
 * `visitor-kiosk` does for `visitor.visit.self-check-out`.
 */
export const DEVICE_ONLY_PERMISSIONS = new Set([
    'visitor.visit.self-check-out',
    'visitor.badge.print',
]);
/**
 * Held only by `PARTNER_ROLES` (docs/adr/0035). `SYSTEM_ROLES` and `DEVICE_ROLES` expansion
 * skips them, so `tenant.owner`'s `platform.*` cannot sweep them up, and `assertRolesAreLive()`
 * fails the process if either map names one literally.
 */
export const PARTNER_ONLY_PERMISSIONS = new Set([
    'platform.partner-customer.read',
    'platform.partner-customer.create',
    'platform.partner-setup.create',
    'platform.partner-team.manage',
    'platform.partner-statement.read',
    'platform.partner-billing.manage',
]);
/**
 * Never held by a `PARTNER_SETUP_ROLES` role — "the customer owns the data" (docs/adr/0035
 * decision 5). Derived from the vocabulary by predicate, so a future `visitor.visit.*`,
 * `visitor.badge.*` or `platform.person.*` permission is excluded without anyone remembering to.
 */
export const PARTNER_SETUP_EXCLUDED_PERMISSIONS = new Set(PERMISSIONS.filter((p) => p.startsWith('visitor.visit.') ||
    p.startsWith('visitor.badge.') ||
    p.startsWith('platform.person.') ||
    p.startsWith('platform.partner-access.') ||
    // Employee data is customer data (adr/0035 decision 5).
    p.startsWith('directory.person.') ||
    p.startsWith('directory.profile.') ||
    p.startsWith('visitor.hosted-visit.') ||
    p.startsWith('platform.push-installation.') ||
    p.startsWith('platform.directory-sync.') ||
    p.startsWith('platform.sso.') ||
    p.startsWith('platform.email-domain.') ||
    p === 'platform.import.manage' ||
    p === 'platform.audit.read'));
/** Narrows an arbitrary string to a `Permission`. Used at trust boundaries. */
export function isPermission(value) {
    return PERMISSION_SET.has(value);
}
/**
 * The trailing `<action>` segment of a permission. Total, because every member
 * of `PERMISSIONS` is validated to have three segments by the hygiene test.
 */
export function permissionAction(permission) {
    const action = permission.slice(permission.lastIndexOf('.') + 1);
    return action;
}
//# sourceMappingURL=permissions.js.map